import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const DEFAULT_APP_NAME = 'PrimeClean';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const AppSettingsContext = createContext({ appName: DEFAULT_APP_NAME, clientName: '', logoUrl: '', refreshAppSettings: () => {} });

export const useAppSettings = () => useContext(AppSettingsContext);

export const AppSettingsProvider = ({ children }) => {
  const [appName, setAppName] = useState(DEFAULT_APP_NAME);
  const [clientName, setClientName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  const [hasAppIcon, setHasAppIcon] = useState(false);

  const refreshAppSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/settings/public`);
      setAppName(data?.data?.app_name || DEFAULT_APP_NAME);
      setClientName(data?.data?.client_name || '');
      // The app's own logo leads the sidebar and login screen; the client logo stands in until one is uploaded.
      const logo = data?.data?.report_primeclean_logo || data?.data?.report_client_logo || '';
      setLogoUrl(logo);
      setFaviconUrl(data?.data?.app_favicon || logo);
      setHasAppIcon(Boolean(data?.data?.has_app_icon));
    } catch {
      setAppName(DEFAULT_APP_NAME);
      setClientName('');
      setLogoUrl('');
      setFaviconUrl('');
      setHasAppIcon(false);
    }
  }, []);

  useEffect(() => {
    refreshAppSettings();
  }, [refreshAppSettings]);

  useEffect(() => {
    document.title = `${appName} - Cleaning Validation Management`;
  }, [appName]);

  // Browser tab icon: the uploaded favicon, or the logo until one is set.
  // Home-screen icon and install manifest come from the server, which applies
  // the same fallbacks (app icon -> favicon -> logo). The version stamp makes
  // the browser re-fetch them after a new upload.
  useEffect(() => {
    const setLink = (rel, href) => {
      let link = document.querySelector(`link[rel="${rel}"]`);
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        document.head.appendChild(link);
      }
      link.href = href;
    };

    if (faviconUrl) setLink('icon', faviconUrl);
    if (hasAppIcon) {
      const version = Date.now();
      setLink('apple-touch-icon', `${API_BASE_URL}/settings/app-icon?v=${version}`);
      setLink('manifest', `${API_BASE_URL}/settings/manifest?v=${version}`);
    }
  }, [faviconUrl, hasAppIcon]);

  return (
    <AppSettingsContext.Provider value={{ appName, clientName, logoUrl, refreshAppSettings }}>
      {children}
    </AppSettingsContext.Provider>
  );
};

export default AppSettingsContext;
